import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { money, num } from "./shared";
import {
  addMonths,
  clamp,
  monthLabel,
  monthsBetween,
  PRESETS,
  presetRange,
  periodLabel,
  type Month,
  type Period,
  type PresetId,
} from "./period";
import type { MonthlySummaryMonth } from "./types";

const MONTH = 84; // px per month: a fixed scale, as a ruler has; a young book's few months sit at the right, by today
const AHEAD = 1; // months drawn after this one, unselectable: room to see that the books end today
const BAR_MAX = 18; // px: the busiest month's activity bar
const DRAG_SLOP = 4; // px of movement before a press on the period becomes a drag rather than a click
const KEY_SETTLE = 500; // ms after the last key before the period (and a history entry) follows the keyed draft
const PAGE = 3; // months PageUp/PageDown move an edge: a quarter

/**
 * D49, after Mercury: a quiet timeline under the page title. Months from the book's start to now, faint income and
 * expense bars behind the ticks, and the period as an outlined box snapped to months: drag its edges to resize, its
 * body to move, click a month for just it (shift-click extends). The chip on the box opens a month-grid picker.
 * Keyboard: the months are one tab stop (arrows walk them, Enter picks, Shift+Enter extends), ←/→ on the box move the
 * whole period, and each edge is a slider (arrows, PageUp/PageDown, Home/End).
 */
export function PeriodRuler({ period, bars, trailing }: { period: Period; bars?: MonthlySummaryMonth[]; trailing?: ReactNode }) {
  const { months } = period;
  const n = months.length;
  const total = n + AHEAD;
  const cells = [...months, ...monthsBetween(addMonths(months[n - 1]!, 1), addMonths(months[n - 1]!, AHEAD))];
  const index = (m: Month) => Math.max(0, months.indexOf(m));
  const committed: [number, number] = [index(period.from), index(period.to) + 1];
  // Month boundaries 0..n: the box runs from the first chosen month's left edge to the last's right edge.
  const [draft, setDraft] = useState<[number, number] | null>(null);
  const [lo, hi] = draft ?? committed;

  const scroller = useRef<HTMLDivElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const drag = useRef<{ mode: "start" | "end" | "move" | "pick"; x: number; from: [number, number]; moved: boolean; month?: Month } | null>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const narrow = useNarrow();
  const keyTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(keyTimer.current), []);
  const cellRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [focusCell, setFocusCell] = useState<number | null>(null);
  const tabCell = Math.min(focusCell ?? lo, n - 1);

  const byMonth = new Map((bars ?? []).map((x) => [(x.month ?? "").slice(0, 7), x]));
  const peak = Math.max(1, ...(bars ?? []).flatMap((x) => [num(x.income), num(x.expenses)]));

  // Keep the period in view: on arrival the ruler opens on it, not on the book's first month.
  useLayoutEffect(() => {
    const el = scroller.current;
    const box = strip.current;
    if (!el || !box) return;
    const col = box.getBoundingClientRect().width / total;
    const left = committed[0] * col;
    const right = committed[1] * col;
    if (left < el.scrollLeft || right > el.scrollLeft + el.clientWidth) el.scrollLeft = Math.max(0, right - el.clientWidth + col * 0.5);
    setCanScrollBack(el.scrollLeft > 1);
  }, [committed[0], committed[1], total]);

  const commit = ([a, b]: [number, number]) => period.set(months[a]!, months[b - 1]!);

  // "pick" presses (outside the box) only ever click; "move" presses (inside it) drag the box when they travel.
  function startDrag(e: PointerEvent, mode: "start" | "end" | "move" | "pick", month?: Month) {
    if (e.button !== 0) return;
    drag.current = { mode, x: e.clientX, from: [lo, hi], moved: false, month };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.stopPropagation();
  }

  function onDrag(e: PointerEvent) {
    const d = drag.current;
    const box = strip.current;
    if (!d || !box || d.mode === "pick") return;
    const dx = e.clientX - d.x;
    if (!d.moved && Math.abs(dx) < DRAG_SLOP) return;
    d.moved = true;
    const step = Math.round(dx / (box.getBoundingClientRect().width / total));
    const [a, b] = d.from;
    if (d.mode === "start") setDraft([Math.min(Math.max(0, a + step), b - 1), b]);
    else if (d.mode === "end") setDraft([a, Math.max(Math.min(n, b + step), a + 1)]);
    else {
      const shift = Math.min(Math.max(step, -a), n - b);
      setDraft([a + shift, b + shift]);
    }
  }

  function endDrag(e: PointerEvent) {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.moved && draft) commit(draft);
    else if (!d.moved && d.month) pick(d.month, e.shiftKey);
    setDraft(null);
  }

  function pick(m: Month, extend: boolean) {
    if (extend) period.set(m < period.from ? m : period.from, m < period.from ? period.to : m);
    else period.set(m, m);
  }

  // Keys move the draft at once; the period follows when they stop, so holding an arrow is one history entry, not ten.
  function keyTo(next: [number, number]) {
    setDraft(next);
    window.clearTimeout(keyTimer.current);
    keyTimer.current = window.setTimeout(() => {
      commit(next);
      setDraft(null);
    }, KEY_SETTLE);
  }

  // Each edge is a slider over the month boundaries, never past the other edge.
  function onEdgeKey(e: KeyboardEvent, edge: "start" | "end") {
    const [a, b] = draft ?? committed;
    const at = edge === "start" ? a : b;
    const target =
      e.key === "ArrowLeft" || e.key === "ArrowDown" ? at - 1
      : e.key === "ArrowRight" || e.key === "ArrowUp" ? at + 1
      : e.key === "PageDown" ? at - PAGE
      : e.key === "PageUp" ? at + PAGE
      : e.key === "Home" ? (edge === "start" ? 0 : a + 1)
      : e.key === "End" ? (edge === "start" ? b - 1 : n)
      : null;
    if (target === null) return;
    e.preventDefault();
    e.stopPropagation();
    keyTo(edge === "start" ? [Math.min(Math.max(0, target), b - 1), b] : [a, Math.max(Math.min(n, target), a + 1)]);
  }

  // ←/→ on the box move the whole period a month.
  function onBoxKey(e: KeyboardEvent) {
    if (e.target !== e.currentTarget) return;
    const k = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0;
    if (!k) return;
    e.preventDefault();
    const [a, b] = draft ?? committed;
    const shift = Math.min(Math.max(k, -a), n - b);
    if (shift) keyTo([a + shift, b + shift]);
  }

  // The months are one tab stop: arrows walk them (their tooltips open on focus), Enter picks, Shift+Enter extends.
  function onCellKey(e: KeyboardEvent, i: number, m: Month) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(m, e.shiftKey);
      return;
    }
    const to = e.key === "ArrowLeft" ? i - 1 : e.key === "ArrowRight" ? i + 1 : e.key === "Home" ? 0 : e.key === "End" ? n - 1 : null;
    if (to === null) return;
    e.preventDefault();
    const j = Math.min(Math.max(to, 0), n - 1);
    setFocusCell(j);
    cellRefs.current[j]?.focus();
  }

  const step = (k: number) => period.set(addMonths(period.from, k), addMonths(period.to, k));
  const label = draft ? periodLabel(months[lo]!, months[hi - 1]!) : period.label;
  const chip = <PeriodChip period={period} label={label} sheet={narrow} />;

  return (
    <>
      <section aria-label="Period" className="mb-5 flex flex-wrap items-end gap-x-1 border-b border-border/70">
        <Button
          variant="ghost"
          size="icon"
          className={cn("mb-1 size-7 shrink-0 text-muted-foreground", !canScrollBack && "invisible")}
          aria-label="Earlier months"
          onClick={() => scroller.current?.scrollBy({ left: -(scroller.current.clientWidth * 0.6), behavior: "smooth" })}
        >
          <ChevronLeft className="size-4" />
        </Button>
        {/* At phone width the box can scroll out of sight, so the chip stands outside the ruler. */}
        {narrow ? <div className="mb-2 shrink-0">{chip}</div> : null}

        <div
          ref={scroller}
          className="min-w-0 flex-1 basis-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onScroll={(e) => setCanScrollBack(e.currentTarget.scrollLeft > 1)}
        >
          <div
            ref={strip}
            className="relative ml-auto flex h-14 touch-pan-x select-none"
            style={{ width: total * MONTH }}
            onPointerMove={onDrag}
            onPointerUp={endDrag}
            onPointerCancel={() => {
              drag.current = null;
              setDraft(null);
            }}
          >
            {cells.map((m, i) => {
              const future = i >= n;
              const bar = byMonth.get(m);
              const inside = i >= lo && i < hi;
              const withYear = i === 0 || m.endsWith("-01");
              const cell = (
                <div
                  key={m}
                  data-month={m}
                  ref={(el) => {
                    cellRefs.current[i] = el;
                  }}
                  {...(future
                    ? {}
                    : {
                        role: "button",
                        tabIndex: i === tabCell ? 0 : -1,
                        "aria-label": monthLabel(m),
                        "aria-pressed": inside,
                        onKeyDown: (e: KeyboardEvent) => onCellKey(e, i, m),
                        onFocus: () => setFocusCell(i),
                      })}
                  onPointerDown={future ? undefined : (e) => startDrag(e, inside ? "move" : "pick", m)}
                  className={cn(
                    "relative flex-1 select-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset",
                    future ? "opacity-40" : inside ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-2 left-2 text-[11px] leading-none whitespace-nowrap tabular-nums",
                      inside ? "font-medium text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {withYear ? monthLabel(m) : monthLabel(m, false)}
                  </span>
                  {/* Activity: faint, low, behind the ticks; it must never compete with the period box. */}
                  <span className="absolute bottom-0 left-1/2 flex -translate-x-1/2 items-end gap-0.5" aria-hidden>
                    <span className="w-1.5 rounded-t-[2px] bg-emerald-500/40" style={{ height: barHeight(bar?.income, peak) }} />
                    <span className="w-1.5 rounded-t-[2px] bg-rose-500/40" style={{ height: barHeight(bar?.expenses, peak) }} />
                  </span>
                  {/* Ticks: the month's start, then its quarters. */}
                  <span className="absolute bottom-0 left-0 h-2.5 w-px bg-border" aria-hidden />
                  {[25, 50, 75].map((p) => (
                    <span key={p} className="absolute bottom-0 h-1 w-px bg-border/80" style={{ left: `${p}%` }} aria-hidden />
                  ))}
                </div>
              );
              return future ? (
                cell
              ) : (
                <Tooltip key={m}>
                  <TooltipTrigger asChild>{cell}</TooltipTrigger>
                  <TooltipContent side="bottom" className="tabular-nums">
                    <p className="font-semibold">{monthLabel(m)}</p>
                    {bar ? (
                      <dl className="mt-1 grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
                        <dt className="flex items-center gap-1.5">
                          <span className="size-2 rounded-[2px] bg-emerald-500" />
                          Income
                        </dt>
                        <dd className="text-right">{money(bar.income)}</dd>
                        <dt className="flex items-center gap-1.5">
                          <span className="size-2 rounded-[2px] bg-rose-500" />
                          Expenses
                        </dt>
                        <dd className="text-right">{money(bar.expenses)}</dd>
                        <dt className="font-semibold">Net</dt>
                        <dd className="text-right font-semibold">{money(bar.net)}</dd>
                      </dl>
                    ) : (
                      <p className="mt-1 opacity-80">No figures yet</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            })}

            {/* The period. Only its edges and chip take the pointer; presses on its body reach the months below. */}
            <div
              data-period-box
              tabIndex={0}
              role="group"
              aria-label={`Period ${label}. Left and right arrows move it a month.`}
              onKeyDown={onBoxKey}
              className="pointer-events-none absolute top-1 bottom-0 rounded-t-md border-[1.5px] border-primary bg-primary/[0.05] transition-[left,width] duration-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none dark:bg-primary/[0.08]"
              style={{ left: `${(lo / total) * 100}%`, width: `${((hi - lo) / total) * 100}%` }}
            >
              {/* Sticky, so a period wider than the visible months still shows its chip. */}
              {!narrow ? <div className="pointer-events-auto sticky left-1 -mt-px ml-1 w-fit">{chip}</div> : null}
              {(["start", "end"] as const).map((edge) => (
                <div
                  key={edge}
                  role="slider"
                  tabIndex={0}
                  aria-label={edge === "start" ? "Period start" : "Period end"}
                  aria-valuemin={edge === "start" ? 0 : lo + 1}
                  aria-valuemax={edge === "start" ? hi - 1 : n}
                  aria-valuenow={edge === "start" ? lo : hi}
                  aria-valuetext={edge === "start" ? `From ${monthLabel(months[lo]!)}` : `To ${monthLabel(months[hi - 1]!)}`}
                  onPointerDown={(e) => startDrag(e, edge)}
                  onKeyDown={(e) => onEdgeKey(e, edge)}
                  className={cn(
                    "group pointer-events-auto absolute inset-y-0 flex w-3 cursor-ew-resize touch-none items-center justify-center focus-visible:outline-none",
                    edge === "start" ? "-left-1.5" : "-right-1.5",
                  )}
                >
                  <span className="h-5 w-1 rounded-full bg-primary ring-primary/30 transition-shadow group-hover:ring-4 group-focus-visible:ring-4" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-1 flex shrink-0 items-center">
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground"
            aria-label="Previous month"
            disabled={period.from <= months[0]!}
            onClick={() => step(-1)}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground"
            aria-label="Next month"
            disabled={period.to >= months[n - 1]!}
            onClick={() => step(1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
        {/* In a narrow pane (chat open, or a phone) the ruler needs the whole row, so anything beside it goes above. */}
        {trailing ? (
          <div className="order-first mb-2 flex basis-full justify-end @3xl:order-none @3xl:mb-1.5 @3xl:ml-1 @3xl:basis-auto @3xl:shrink-0">
            {trailing}
          </div>
        ) : null}
      </section>
    </>
  );
}

function barHeight(value: number | string | undefined, peak: number) {
  const v = num(value);
  return v > 0 ? Math.max(2, Math.round((v / peak) * BAR_MAX)) : 0;
}

function useNarrow() {
  const query = "(max-width: 639px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

/** The accent chip on the period box; opens the picker (a sheet at phone width). */
function PeriodChip({ period, label, sheet }: { period: Period; label: string; sheet: boolean }) {
  const [open, setOpen] = useState(false);
  const trigger = (
    <button
      type="button"
      aria-label={`Period: ${label}. Change`}
      onClick={sheet ? () => setOpen(true) : undefined}
      className="inline-flex items-center gap-1 rounded-[5px] bg-primary px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap text-primary-foreground shadow-sm tabular-nums focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none"
    >
      <CalendarDays className="size-3" />
      {label}
      <ChevronDown className="size-3" />
    </button>
  );
  const picker = <PeriodPicker period={period} onDone={() => setOpen(false)} />;

  if (sheet) {
    return (
      <>
        {trigger}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="gap-0 rounded-t-xl">
            <SheetHeader className="pb-0">
              <SheetTitle>Period</SheetTitle>
            </SheetHeader>
            <div className="p-4">{picker}</div>
          </SheetContent>
        </Sheet>
      </>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="w-[25rem] p-4">
        {picker}
      </PopoverContent>
    </Popover>
  );
}

const CUSTOM = "custom";

/**
 * Month by month: a preset, From and To, and two years of months side by side. A first click sets the start, the next
 * the end (either order); months outside the books are disabled. Nothing changes until Save.
 */
function PeriodPicker({ period, onDone }: { period: Period; onDone: () => void }) {
  const first = period.months[0]!;
  const last = period.months[period.months.length - 1]!;
  const [range, setRange] = useState<[Month, Month]>([period.from, period.to]);
  const [anchor, setAnchor] = useState<Month | null>(null);
  const [year, setYear] = useState(Number(period.to.slice(0, 4)));
  const preset = PRESETS.find((p) => {
    const r = presetRange(p.id, first, last, period.fyStart);
    return r && r[0] === range[0] && r[1] === range[1];
  })?.id;

  function click(m: Month) {
    if (!anchor) {
      setAnchor(m);
      setRange([m, m]);
    } else {
      setRange(m < anchor ? [m, anchor] : [anchor, m]);
      setAnchor(null);
    }
  }

  function applyPreset(id: string) {
    if (id === CUSTOM) return;
    const r = presetRange(id as PresetId, first, last, period.fyStart);
    if (r) {
      setRange(r);
      setAnchor(null);
      setYear(Number(r[1].slice(0, 4)));
    }
  }

  const firstYear = Number(first.slice(0, 4));
  const lastYear = Number(last.slice(0, 4));

  return (
    <div className="space-y-4">
      <Select value={preset ?? CUSTOM} onValueChange={applyPreset}>
        <SelectTrigger className="w-full" aria-label="Preset">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={CUSTOM}>Custom</SelectItem>
          {PRESETS.map((p) => (
            <SelectItem key={p.id} value={p.id} disabled={!presetRange(p.id, first, last, period.fyStart)}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <Field label="From" active={anchor === null}>
          {monthLabel(range[0])}
        </Field>
        <span className="pb-2 text-muted-foreground">-</span>
        <Field label="To" active={anchor !== null}>
          {range[1] === last ? `${monthLabel(range[1])} (to date)` : monthLabel(range[1])}
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {[year - 1, year].map((y, i) => (
          <div key={y}>
            <div className="mb-2 flex h-6 items-center justify-between text-xs font-semibold tabular-nums">
              {i === 0 ? (
                <Button variant="ghost" size="icon" className="size-6" aria-label="Earlier year" disabled={y <= firstYear} onClick={() => setYear(year - 1)}>
                  <ChevronLeft className="size-3.5" />
                </Button>
              ) : (
                <span className="size-6" />
              )}
              <span>{y}</span>
              {i === 1 ? (
                <Button variant="ghost" size="icon" className="size-6" aria-label="Later year" disabled={y >= lastYear} onClick={() => setYear(year + 1)}>
                  <ChevronRight className="size-3.5" />
                </Button>
              ) : (
                <span className="size-6" />
              )}
            </div>
            <div className="grid grid-cols-3 gap-1" role="group" aria-label={String(y)}>
              {Array.from({ length: 12 }, (_, k) => `${y}-${String(k + 1).padStart(2, "0")}`).map((m) => {
                const out = m < first || m > last;
                const end = m === range[0] || m === range[1];
                const inside = m > range[0] && m < range[1];
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={out}
                    aria-pressed={end || inside}
                    aria-label={monthLabel(m)}
                    onClick={() => click(m)}
                    className={cn(
                      "h-8 rounded-md text-xs tabular-nums transition-colors disabled:pointer-events-none disabled:opacity-35",
                      end
                        ? "bg-primary font-semibold text-primary-foreground"
                        : inside
                          ? "bg-primary/15 text-foreground"
                          : "text-foreground hover:bg-muted",
                    )}
                  >
                    {monthLabel(m, false)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => {
            const r = clamp(range[0], range[1], first, last);
            if (r) period.set(r[0], r[1]);
            onDone();
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function Field({ label, active, children }: { label: string; active: boolean; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={cn(
          "flex h-9 items-center rounded-md border px-3 text-sm tabular-nums",
          active ? "border-primary ring-2 ring-primary/15" : "bg-muted/30",
        )}
      >
        {children}
      </p>
    </div>
  );
}
